import Controller from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { didCancel, task } from 'ember-concurrency';
import type { BulkApplyFilters } from 'lumen/adapters/subscription-user';
import type { SubscriptionOption } from 'lumen/components/settings/users/subscription-select';
import type {
  BulkApplyChange,
  BulkDeleteChange,
  UserChange,
} from 'lumen/components/settings/users/tables/users-table';
import type ManagedUser from 'lumen/models/managed-user';
import type SubscriptionUser from 'lumen/models/subscription-user';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import type { InvitePayload, RouteParams } from 'lumen/services/settings-users';
import { PRODUCTS } from 'lumen/utils/constants/users-constants';
import { timeout } from 'lumen/utils/waiters';

type SortableColumn = 'name' | 'role';

function isProductChanged(payload: BulkApplyChange, product: string): boolean {
  return (
    payload.addProducts?.includes(product) === true ||
    payload.removeProducts?.includes(product) === true
  );
}

export default class HotelSettingsUsersController extends Controller {
  queryParams = [
    'sortBy',
    'sortDirection',
    { search: 'q' },
    'role',
    'product',
    'subscription',
  ];

  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare settingsUsers: Services['settings-users'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked accumulatedManagedUsers: ManagedUser[] = [];
  @tracked accumulatedUsers: SubscriptionUser[] = [];
  @tracked currentPage = 1;
  @tracked isInviteModalVisible = false;
  @tracked product: string | null = null;
  @tracked role: string | null = null;
  @tracked search: string | null = null;
  @tracked searchInputValue: string | null = null;
  @tracked selectedSubscriptionOption: SubscriptionOption | null = null;
  @tracked sortBy: SortableColumn = 'name';
  @tracked sortDirection: 'asc' | 'desc' = 'asc';
  @tracked subscription: string | null = null;
  @tracked totalRecords = 0;

  debouncedSearchTask = task({ restartable: true }, async (value: string) => {
    await timeout();

    const trimmed = value.trim();
    if (!trimmed) {
      this.search = null;
    } else {
      this.search = trimmed
        .split(',')
        .map((name) => name.trim())
        .filter(Boolean)
        .join(',');
    }

    this.usageAnalytics.track('User Access Table Filtered', {
      eventTrigger: 'inline-filters',
      product: 'user-access',
      filterType: 'name',
      value: this.search ?? '',
    });
  });

  loadMoreTask = task({ drop: true }, async (): Promise<void> => {
    if (
      !this.hasMore ||
      this.settingsUsers.loadUsersTask.isRunning ||
      this.settingsUsers.loadManagedUsersTask.isRunning
    ) {
      return;
    }

    const params = this.buildLoadParams(this.currentPage + 1);

    if (this.isAllSubscriptions) {
      const result =
        await this.settingsUsers.loadManagedUsersTask.perform(params);

      if (result) {
        this.accumulatedManagedUsers = [
          ...this.accumulatedManagedUsers,
          ...result.users,
        ];
        this.totalRecords = result.meta.total_items;
        this.currentPage = this.currentPage + 1;
      } else {
        this.totalRecords = this.accumulatedManagedUsers.length;
      }
      return;
    }

    const result = await this.settingsUsers.loadUsersTask.perform(params);

    if (result) {
      this.accumulatedUsers = [...this.accumulatedUsers, ...result.users];
      this.totalRecords = result.meta.total_items;
      this.currentPage = this.currentPage + 1;
    } else {
      this.totalRecords = this.accumulatedUsers.length;
    }
  });

  get activeSubscription(): string | null {
    return this.isReadOnlyView
      ? this.readOnlySubscriptionId
      : this.subscription;
  }

  private get bulkFilters(): BulkApplyFilters {
    const filters: BulkApplyFilters = {};

    if (this.role) {
      filters.role = this.role;
    }
    if (this.product) {
      filters.product = this.product;
    }
    if (this.search) {
      filters.q = this.search;
    }

    return filters;
  }

  get displaySearchValue(): string {
    if (this.searchInputValue !== null) {
      return this.searchInputValue;
    }

    return this.search ?? '';
  }

  get hasInlineFilters(): boolean {
    return !this.isAllSubscriptions && !this.isReadOnlyView;
  }

  get hasMore(): boolean {
    const loadedCount = this.isAllSubscriptions
      ? this.accumulatedManagedUsers.length
      : this.accumulatedUsers.length;
    return loadedCount < this.totalRecords;
  }

  get isAdmin(): boolean {
    return this.settingsUsers.isAdminForSubscription(this.subscription);
  }

  get isAllSubscriptions(): boolean {
    return this.activeSubscription === null;
  }

  get isLoading(): boolean {
    // Pagination reports through isLoadingMore: swapping the table body for the
    // loading skeleton collapses the scroll container and jumps back to the top.
    if (this.isLoadingMore) {
      return false;
    }

    return (
      (this.isAllSubscriptions
        ? this.settingsUsers.loadManagedUsersTask.isRunning
        : this.settingsUsers.loadUsersTask.isRunning ||
          this.settingsUsers.loadSubscriptionAdminTask.isRunning) ?? false
    );
  }

  get isLoadingMore(): boolean {
    return this.loadMoreTask.isRunning;
  }

  get isReadOnlyView(): boolean {
    return (
      !this.settingsUsers.canManageUsers && this.readOnlySubscriptionId !== null
    );
  }

  // Editors and viewers can only look up who the admins of the hotel they're
  // viewing are.
  get readOnlySubscriptionId(): string | null {
    return this.hotelAndMarketService.hotel.id;
  }

  get selectedSubscription(): SubscriptionOption | null {
    return this.selectedSubscriptionOption;
  }

  get sorts(): { isAscending: boolean; valuePath: SortableColumn }[] {
    return [
      {
        // The managed-users endpoint only sorts on `name`, so a `role` sort
        // carried over from the subscription view must not render as active.
        valuePath: this.isAllSubscriptions ? 'name' : this.sortBy,
        isAscending: this.sortDirection === 'asc',
      },
    ];
  }

  buildLoadParams(page: number): RouteParams {
    return {
      page,
      sortBy: this.sortBy,
      sortDirection: this.sortDirection,
      search: this.search ?? undefined,
      role: this.hasInlineFilters ? (this.role ?? undefined) : undefined,
      product: this.hasInlineFilters ? (this.product ?? undefined) : undefined,
      subscription: this.activeSubscription ?? undefined,
    };
  }

  async resetAndLoad(): Promise<void> {
    if (this.subscription) {
      void this.settingsUsers.loadSubscriptionAdminTask.perform(
        this.subscription,
      );
    }
    this.accumulatedManagedUsers = [];
    this.accumulatedUsers = [];
    this.currentPage = 1;
    this.totalRecords = 0;
    this.searchInputValue = this.search;

    const params = this.buildLoadParams(1);

    try {
      if (this.subscription && !this.selectedSubscriptionOption) {
        this.selectedSubscriptionOption =
          (await this.settingsUsers.loadSubscriptionOptionTask.perform(
            this.subscription,
          )) ?? null;
      }

      if (this.isAllSubscriptions) {
        const result =
          await this.settingsUsers.loadManagedUsersTask.perform(params);

        if (result) {
          this.accumulatedManagedUsers = result.users;
          this.totalRecords = result.meta.total_items;
        }
        return;
      }

      const result = await this.settingsUsers.loadUsersTask.perform(params);

      if (result) {
        this.accumulatedUsers = result.users;
        this.totalRecords = result.meta.total_items;
      }
    } catch (error) {
      // Ignore ember concurrency task cancellation errors because the load tasks are restartable and get canceled when a new load starts or the route is torn down.
      if (!didCancel(error)) {
        // But re-throw the non-cancelation error
        throw error;
      }
    }
  }

  @action
  async applyBulkChanges(payload: BulkApplyChange): Promise<boolean> {
    if (!this.subscription) {
      return false;
    }

    const success = await this.settingsUsers.applyBulkChanges(
      this.subscription,
      payload,
      this.bulkFilters,
    );

    if (success) {
      this.usageAnalytics.track('User Access Bulk Edit Applied', {
        eventTrigger: 'bulk-action-row',
        product: 'user-access',
        numberOfUsers: payload.selectedCount,
        isSelectAllActive: true,
        permissionLevelChanged: payload.role !== undefined,
        riAccessChanged: isProductChanged(payload, PRODUCTS.RATE_INSIGHT),
        biAccessChanged: isProductChanged(payload, PRODUCTS.BI_BENCHMARKING),
      });

      await this.resetAndLoad();
    }

    return success;
  }

  @action
  async bulkDeleteUsers(payload: BulkDeleteChange): Promise<boolean> {
    if (!this.subscription) {
      return false;
    }

    const success = await this.settingsUsers.bulkDeleteUsers(
      this.subscription,
      payload,
      this.bulkFilters,
      this.selectedSubscriptionOption?.label ?? '',
    );

    if (success) {
      this.usageAnalytics.track('User Access Users Bulk Deleted', {
        eventTrigger: 'bulk-action-row',
        product: 'user-access',
        numberOfUsers: payload.selectedCount,
        isSelectAllActive: payload.isSelectAllActive,
      });

      await this.resetAndLoad();
    }

    return success;
  }

  @action
  closeInviteModal(): void {
    this.isInviteModalVisible = false;
  }

  @action
  async deleteUser(user: SubscriptionUser): Promise<void> {
    if (!this.subscription) {
      return;
    }

    const success = await this.settingsUsers.deleteUser(
      this.subscription,
      user,
      this.selectedSubscriptionOption?.label ?? '',
    );

    if (success) {
      this.usageAnalytics.track('User Access User Deleted', {
        eventTrigger: 'users-table',
        product: 'user-access',
      });

      await this.resetAndLoad();
    }
  }

  @action
  async inviteUsers(payload: InvitePayload): Promise<boolean> {
    const success = await this.settingsUsers.inviteUsers(payload);

    if (success) {
      await this.resetAndLoad();
    }

    return success;
  }

  @action
  onUpdateSorts(sorts: { isAscending: boolean; valuePath: string }[]): void {
    if (sorts[0]) {
      this.sortBy = sorts[0].valuePath as SortableColumn;
      this.sortDirection = sorts[0].isAscending ? 'asc' : 'desc';
    } else {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    }
  }

  @action
  openInviteModal(): void {
    this.isInviteModalVisible = true;
  }

  @action
  async removeUserFromAllSubscriptions(user: ManagedUser): Promise<void> {
    const success =
      await this.settingsUsers.removeUserFromAllSubscriptions(user);

    if (success) {
      this.usageAnalytics.track('User Access User Deleted', {
        eventTrigger: 'users-table-all-subscriptions',
        product: 'user-access',
      });

      await this.resetAndLoad();
    }
  }

  @action
  async saveChanges(changes: UserChange[]): Promise<boolean> {
    if (!this.subscription) {
      return false;
    }

    const success = await this.settingsUsers.saveChanges(
      this.subscription,
      changes,
    );

    if (success) {
      this.usageAnalytics.track('User Access Bulk Edit Applied', {
        eventTrigger: 'bulk-edit',
        product: 'user-access',
        numberOfUsers: changes.length,
        isSelectAllActive: false,
        permissionLevelChanged: changes.some(
          (change) => change.newRole !== change.originalRole,
        ),
        riAccessChanged: changes.some(
          (change) =>
            change.newRateAndMarketInsight !==
            change.originalRateAndMarketInsight,
        ),
        biAccessChanged: changes.some(
          (change) =>
            change.newBiBenchmarking !== change.originalBiBenchmarking,
        ),
      });

      await this.resetAndLoad();
    }

    return success;
  }

  @action
  updateProduct(product: string | null): void {
    this.product = product;

    this.usageAnalytics.track('User Access Table Filtered', {
      eventTrigger: 'inline-filters',
      product: 'user-access',
      filterType: 'product',
      value: product ?? 'all',
    });
  }

  @action
  updateRole(role: string | null): void {
    this.role = role;
    this.accumulatedManagedUsers = [];
    this.accumulatedUsers = [];
    this.currentPage = 1;
    this.totalRecords = 0;

    this.usageAnalytics.track('User Access Table Filtered', {
      eventTrigger: 'inline-filters',
      product: 'user-access',
      filterType: 'role',
      value: role ?? 'all',
    });
  }

  @action
  updateSearch(event: Event): void {
    this.searchInputValue = (event.target as HTMLInputElement).value;
    void this.debouncedSearchTask.perform(this.searchInputValue);
  }

  @action
  updateSubscription(selectedOption: SubscriptionOption | null): void {
    this.subscription = selectedOption?.id ?? null;
    this.selectedSubscriptionOption = selectedOption;

    if (this.isAllSubscriptions) {
      this.role = null;
      this.product = null;
    }

    this.usageAnalytics.track('User Access Table Filtered', {
      eventTrigger: 'inline-filters',
      product: 'user-access',
      filterType: 'subscription',
      value: this.subscription ?? 'all',
    });
  }
}
